import { useCallback, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { toast } from "@/lib/toast";

// Choosing a plan PDF, in ONE implementation.
//
// Why this exists: the updated plan could be dropped onto its card, and the
// approved set could only be browsed for through a small button — so the two
// halves of the same question answered differently, and an operator who had just
// dragged one PDF in had no way to drag the other. It also drifted: the review
// page's picker validated the extension and cleared the input so the same file
// could be re-chosen, and the upload page's did neither, which silently sent a
// .dwg to the server and refused to re-pick a corrected file of the same name.
//
// Every plan-PDF control now takes both gestures from here: spread `dropProps`
// on whatever REGION should accept a drop (the whole card, not just the button —
// that is the part that was missing) and `inputProps` on the hidden input inside
// the browse label.
export function usePlanPdfDrop(
  onFile: (file: File) => void,
  disabled = false,
): {
  /** True while a drag is over the region — for the highlight. */
  dragging: boolean;
  dropProps: {
    onDragEnter: (e: DragEvent) => void;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
  inputProps: {
    type: "file";
    accept: string;
    className: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  };
} {
  const [dragging, setDragging] = useState(false);
  // How deep into the region the drag currently is. A card with a heading, a
  // hint and a button is nothing but child elements, and each one entered fires
  // a leave on the one before it — counted, the highlight holds; not counted, it
  // strobes and reads as a dead drop target.
  const depth = useRef(0);

  const take = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;
      if (!file.name.toLowerCase().endsWith(".pdf")) {
        toast.error("Please choose a PDF plan document.");
        return;
      }
      onFile(file);
    },
    [onFile],
  );

  return {
    dragging: dragging && !disabled,
    dropProps: {
      onDragEnter: (e) => {
        e.preventDefault();
        depth.current += 1;
        if (!disabled) setDragging(true);
      },
      // Required on its own: a region that does not cancel dragover is not a
      // drop target at all, and the browser refuses the drop.
      onDragOver: (e) => {
        e.preventDefault();
        if (!disabled && !dragging) setDragging(true);
      },
      onDragLeave: () => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setDragging(false);
      },
      onDrop: (e) => {
        e.preventDefault();
        depth.current = 0;
        setDragging(false);
        if (disabled) return;
        take(e.dataTransfer.files?.[0]);
      },
    },
    inputProps: {
      type: "file",
      accept: ".pdf",
      className: "sr-only",
      onChange: (e) => {
        const file = e.target.files?.[0] ?? null;
        // Clear it so re-choosing the SAME filename fires onChange again — the
        // operator who exported a corrected set over the old file otherwise
        // clicks and nothing happens.
        e.target.value = "";
        take(file);
      },
    },
  };
}
